import { motion, AnimatePresence } from "motion/react";
import { Icon } from "../../components/Icon";
import { useT } from "../../i18n/strings";
import {
  kindLabels,
  labelOf,
  metalLabels,
  ownershipLabels,
  stoneTypeLabels,
} from "../../i18n/choices";
import { micro } from "../../motion";
import { haptic } from "../../telegram/haptics";
import type { Filters } from "./filterState";
import s from "./filters.module.css";

interface ActiveChip {
  key: string;
  label: string;
  clear: Partial<Filters>;
}

/**
 * What is filtering the list, spelled out: a chip per filter answers "which
 * ones?" in place and lets one be dropped without opening the sheet.
 */
export function ActiveFilters({
  filters,
  onChange,
  onClearAll,
}: {
  filters: Filters;
  onChange: (changes: Partial<Filters>) => void;
  onClearAll: () => void;
}) {
  const t = useT();
  const chips: ActiveChip[] = [];

  for (const kind of filters.kind) {
    chips.push({
      key: `kind-${kind}`,
      label: labelOf(kindLabels, kind),
      clear: { kind: filters.kind.filter((other) => other !== kind) },
    });
  }
  for (const metal of filters.metal) {
    chips.push({
      key: `metal-${metal}`,
      label: labelOf(metalLabels, metal),
      clear: { metal: filters.metal.filter((other) => other !== metal) },
    });
  }
  for (const status of filters.ownership_status) {
    chips.push({
      key: `status-${status}`,
      label: labelOf(ownershipLabels, status),
      clear: { ownership_status: filters.ownership_status.filter((other) => other !== status) },
    });
  }
  if (filters.purity !== null) {
    chips.push({
      key: "purity",
      label: t.filters.purityChip(filters.purity),
      clear: { purity: null },
    });
  }
  if (filters.stone) {
    chips.push({
      key: "stone",
      label: labelOf(stoneTypeLabels, filters.stone),
      clear: { stone: null },
    });
  }
  if (filters.has_stones !== null) {
    chips.push({
      key: "has_stones",
      label: filters.has_stones ? t.filters.withStones : t.filters.withoutStones,
      clear: { has_stones: null },
    });
  }

  if (!chips.length) return null;

  return (
    <div className={s.activeRow}>
      <div className={s.activeChips}>
        <AnimatePresence initial={false} mode="popLayout">
          {chips.map((chip) => (
            <motion.button
              key={chip.key}
              type="button"
              layout
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={micro}
              className={s.activeChip}
              aria-label={t.filters.removeOne(chip.label)}
              onClick={() => {
                haptic.select();
                onChange(chip.clear);
              }}
            >
              <span className={s.activeChipLabel}>{chip.label}</span>
              <Icon name="close" size={16} />
            </motion.button>
          ))}
        </AnimatePresence>
      </div>

      {chips.length > 1 && (
        <button
          type="button"
          className={s.clearAllButton}
          onClick={() => {
            haptic.tap();
            onClearAll();
          }}
        >
          {t.actions.clearAll}
        </button>
      )}
    </div>
  );
}
